import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Service {
  name: string
  status: string
  uptime: number
  response_time: number | null
}

interface Alert {
  id: string
  severity: string
  service: string
  message: string
  timestamp: string
  resolved: boolean
}

export default function SystemHealthPage() {
  const [services, setServices] = useState<Service[]>([])
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [, setMetrics] = useState<any[]>([])
  const [currentMetrics, setCurrentMetrics] = useState<any>(null)
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchCurrentMetrics, 5000)
    return () => clearInterval(interval)
  }, [])

  const fetchData = async () => {
    const [servRes, alertRes, histRes, currRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/system-health/services'),
      fetch(API_BASE + '/system-health/alerts?include_resolved=true'),
      fetch(API_BASE + '/system-health/metrics/history'),
      fetch(API_BASE + '/system-health/metrics/current'),
      fetch(API_BASE + '/system-health/statistics')
    ])
    if (servRes.ok) setServices((await servRes.json()).services || [])
    if (alertRes.ok) setAlerts((await alertRes.json()).alerts || [])
    if (histRes.ok) setMetrics((await histRes.json()).metrics || [])
    if (currRes.ok) setCurrentMetrics(await currRes.json())
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const fetchCurrentMetrics = async () => {
    const res = await fetch(API_BASE + '/system-health/metrics/current')
    if (res.ok) setCurrentMetrics(await res.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-100 text-green-800'
      case 'degraded': return 'bg-yellow-100 text-yellow-800'
      case 'down': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300'
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-300'
      case 'info': return 'bg-blue-100 text-blue-800 border-blue-300'
      default: return 'bg-gray-100 text-gray-800 border-gray-300'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">System Health Monitoring</h1>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className={`bg-white rounded-lg shadow p-4 border-l-4 ${statistics.overall_health === 'healthy' ? 'border-green-500' : 'border-red-500'}`}>
              <div className="text-sm text-gray-500">Overall Health</div>
              <div className={`text-2xl font-bold capitalize ${statistics.overall_health === 'healthy' ? 'text-green-600' : 'text-red-600'}`}>{statistics.overall_health}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Services</div>
              <div className="text-2xl font-bold">{statistics.services_healthy}/{statistics.services_total}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">CPU Usage</div>
              <div className={`text-2xl font-bold ${statistics.cpu_usage > 80 ? 'text-red-600' : 'text-green-600'}`}>{statistics.cpu_usage?.toFixed(1)}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Memory Usage</div>
              <div className={`text-2xl font-bold ${statistics.memory_usage > 80 ? 'text-red-600' : 'text-green-600'}`}>{statistics.memory_usage?.toFixed(1)}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Disk Usage</div>
              <div className={`text-2xl font-bold ${statistics.disk_usage > 80 ? 'text-red-600' : 'text-green-600'}`}>{statistics.disk_usage?.toFixed(1)}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Uptime</div>
              <div className="text-2xl font-bold text-green-600">{statistics.avg_uptime?.toFixed(2)}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Services Status</h2></div>
            <div className="divide-y">
              {services.map(service => (
                <div key={service.name} className="p-4 flex justify-between items-center">
                  <div>
                    <div className="font-semibold">{service.name}</div>
                    <div className="text-sm text-gray-500">Uptime: {service.uptime}%</div>
                  </div>
                  <div className="flex items-center gap-4">
                    {service.response_time !== null && (
                      <span className="text-sm text-gray-500">{service.response_time}ms</span>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(service.status)}`}>{service.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {currentMetrics && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Live Metrics</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>CPU</span>
                    <span>{currentMetrics.cpu_usage?.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${currentMetrics.cpu_usage > 80 ? 'bg-red-500' : 'bg-blue-600'}`} style={{ width: `${currentMetrics.cpu_usage}%` }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Memory</span>
                    <span>{currentMetrics.memory_usage?.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${currentMetrics.memory_usage > 80 ? 'bg-red-500' : 'bg-green-600'}`} style={{ width: `${currentMetrics.memory_usage}%` }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Disk</span>
                    <span>{currentMetrics.disk_usage?.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${currentMetrics.disk_usage > 80 ? 'bg-red-500' : 'bg-purple-600'}`} style={{ width: `${currentMetrics.disk_usage}%` }}></div>
                  </div>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Active Connections</span>
                    <span className="font-medium">{currentMetrics.active_connections}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Requests/sec</span>
                    <span className="font-medium">{currentMetrics.requests_per_sec?.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Alerts</h2></div>
          <div className="divide-y">
            {alerts.map(alert => (
              <div key={alert.id} className={`p-4 border-l-4 ${getSeverityColor(alert.severity)}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold">{alert.service}</div>
                    <div className="text-sm text-gray-600">{alert.message}</div>
                    <div className="text-xs text-gray-400 mt-1">{new Date(alert.timestamp).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${getSeverityColor(alert.severity)}`}>{alert.severity}</span>
                    {alert.resolved && <span className="text-green-600 text-xs">Resolved</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
